import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useReducer,
} from 'react';
import { useApolloClient } from '@apollo/client';
import { v1 as uuid } from 'uuid';
import { MultiKey } from '../core/MultiKey';
import type { PortCostData, PortCostPair, MarcuraPortCosts } from './context';
import { MarcuraPortCostContext } from './context';
import { PortCostMap } from './portCostMap';
import { emptyPortCostData } from './constant';
import { getStoredPortCosts, setStoredPortCosts } from './store';
import { useNotify } from '../notifications/ToastNotifications';
import {
  GetMarcuraPortCostsDocument,
  type GetMarcuraPortCostsMutation,
  type GetMarcuraPortCostsMutationVariables,
} from '../../graphql';
import type { LocationId } from '@seaber/common/core/entries';
import { portCostPairKey } from '@seaber/common/lib/marcura';

// Marcura serializes at 5/credential server-side
const FETCH_BATCH_SIZE = 100;

interface MarcuraPortCostProviderProps {
  children: React.ReactNode;
}

type PortCostResponse = { response: PortCostData };
type PortCostError = { portCostPair: PortCostPair; error: string };

export interface PortCostsState {
  portCosts: MarcuraPortCosts;
  queue: PortCostPair[];
  errors: PortCostError[];
  loading: boolean;
}

export type PortCostsAction =
  | { type: 'PORT_COSTS_RESET'; portCosts: MarcuraPortCosts }
  | { type: 'ADDED_TO_QUEUE'; portCostPairs: PortCostPair[] }
  | { type: 'RESPONSES_RECEIVED'; responses: PortCostResponse[] }
  | { type: 'ERRORS_RECEIVED'; responses: PortCostError[] }
  | { type: 'LOADING_STARTED' }
  | { type: 'LOADING_ENDED' };

export const initialPortCostMap = new PortCostMap(new MultiKey(2));
export const initialPortCostsState: PortCostsState = {
  portCosts: initialPortCostMap,
  queue: [],
  errors: [],
  loading: false,
};

export const portCostsReducer = (
  state: PortCostsState,
  action: PortCostsAction,
): PortCostsState => {
  switch (action.type) {
    case 'PORT_COSTS_RESET': {
      return {
        ...state,
        portCosts: action.portCosts,
      };
    }
    case 'ADDED_TO_QUEUE': {
      const queue = state.queue;
      const queuedPairs = new Set(
        state.queue.map((pair) => portCostPairKey(pair[0], pair[1])),
      );

      const newPairs = action.portCostPairs.filter((pair) => {
        const isFetched = state.portCosts.get(...pair);
        const key = portCostPairKey(pair[0], pair[1]);
        return !isFetched && !queuedPairs.has(key);
      });

      const requeuedKeys = new Set(
        action.portCostPairs.map((pair) => portCostPairKey(pair[0], pair[1])),
      );
      const filteredErrors = state.errors.filter(
        ({ portCostPair: [locationId, assortmentId] }) =>
          !requeuedKeys.has(portCostPairKey(locationId, assortmentId)),
      );
      const newErrors =
        filteredErrors.length === state.errors.length
          ? state.errors
          : filteredErrors;

      if (newPairs.length === 0 && newErrors === state.errors) {
        return state;
      }

      return {
        ...state,
        queue: [...queue, ...newPairs],
        errors: newErrors,
      };
    }
    case 'RESPONSES_RECEIVED': {
      const responseKeys = new Set(
        action.responses.map((res) =>
          portCostPairKey(res.response.location_id, res.response.assortment_id),
        ),
      );
      const newQueue = state.queue.filter(
        (queueItem) =>
          !responseKeys.has(portCostPairKey(queueItem[0], queueItem[1])),
      );
      const newPortCosts = new PortCostMap(state.portCosts.map);
      action.responses.forEach(({ response }) => newPortCosts.set(response));

      return {
        ...state,
        queue: newQueue,
        portCosts: newPortCosts,
      };
    }
    case 'ERRORS_RECEIVED': {
      const errorKeys = new Set(
        action.responses.map((err) =>
          portCostPairKey(err.portCostPair[0], err.portCostPair[1]),
        ),
      );

      const newQueue = state.queue.filter(
        (queueItem) =>
          !errorKeys.has(portCostPairKey(queueItem[0], queueItem[1])),
      );

      return {
        ...state,
        queue: newQueue,
        errors: [...state.errors, ...action.responses],
      };
    }
    case 'LOADING_STARTED': {
      return {
        ...state,
        loading: true,
      };
    }
    case 'LOADING_ENDED': {
      return {
        ...state,
        loading: false,
      };
    }
    default: {
      return state;
    }
  }
};

export const MarcuraPortCostProvider = ({
  children,
}: MarcuraPortCostProviderProps) => {
  const apolloClient = useApolloClient();
  const notifyLoading = useNotify({
    toastId: 'PORTCOST_LOADING',
    dismissible: true,
  });

  const [state, dispatch] = useReducer(portCostsReducer, initialPortCostsState);
  const { portCosts, queue, loading } = state;
  const drainCtxRef = useRef<{ uuid: string; consumed: number } | null>(null);

  useEffect(() => {
    async function initialize() {
      const storedPortCosts = await getStoredPortCosts();
      dispatch({ type: 'PORT_COSTS_RESET', portCosts: storedPortCosts });
    }
    initialize();
  }, []);

  useEffect(() => {
    if (loading) {
      return;
    }
    const fetcher = async () => {
      const queueSnapshot = queue;
      if (queueSnapshot.length === 0) {
        return;
      }
      // One batch per run; the effect re-fires when queue or loading changes.
      const currentQueue = queueSnapshot.slice(0, FETCH_BATCH_SIZE);
      const isLastBatch = currentQueue.length === queueSnapshot.length;
      drainCtxRef.current ??= { uuid: uuid(), consumed: 0 };
      const { uuid: clientRunUuid, consumed } = drainCtxRef.current;
      const drainTotal = consumed + queueSnapshot.length;

      try {
        dispatch({ type: 'LOADING_STARTED' });
        notifyLoading(`Loading ${queueSnapshot.length} Marcura port costs…`, {
          type: 'info',
          isLoading: true,
        });

        const { data } = await apolloClient.mutate<
          GetMarcuraPortCostsMutation,
          GetMarcuraPortCostsMutationVariables
        >({
          mutation: GetMarcuraPortCostsDocument,
          variables: {
            input: currentQueue.map(([locationId, assortmentId]) => ({
              locationId,
              assortmentId,
            })),
            clientRunUuid,
            drainTotal,
            drainRemaining: queueSnapshot.length,
          },
        });

        const apiResults = data?.getMarcuraPortCosts;
        if (apiResults === null || apiResults === undefined) {
          throw new Error('Port costs data not found');
        }

        const fromApi: PortCostResponse[] = apiResults.flatMap((apiResponse) =>
          apiResponse
            ? [
                {
                  response: {
                    id: apiResponse.id,
                    marcura_port_id: apiResponse.marcura_port_id,
                    marcura_group_id: apiResponse.marcura_group_id,
                    location_id: apiResponse.location_id as LocationId,
                    assortment_id: apiResponse.assortment_id,
                    cached_at: apiResponse.cached_at,
                    data: {
                      costs: Array.isArray(apiResponse.data?.costs)
                        ? apiResponse.data.costs
                            .filter(
                              (cost): cost is NonNullable<typeof cost> =>
                                cost !== null,
                            )
                            .map((cost) => ({
                              ...cost,
                              dwtMax: cost.dwtMax ?? 0,
                              dwtMin: cost.dwtMin ?? 0,
                            }))
                        : [],
                    },
                  },
                },
              ]
            : [],
        );

        // Synthesize no-data placeholders for any pair the resolver did not
        // return so the queue drains and the browser caches the miss.
        const returnedKeys = new Set(
          fromApi.map(({ response }) =>
            portCostPairKey(response.location_id, response.assortment_id),
          ),
        );
        const synthesizedAt = new Date().toISOString();
        const synthesized: PortCostResponse[] = currentQueue
          .filter(
            ([locationId, assortmentId]) =>
              !returnedKeys.has(portCostPairKey(locationId, assortmentId)),
          )
          .map(([location_id, assortment_id]) => ({
            response: {
              ...emptyPortCostData,
              location_id,
              assortment_id,
              cached_at: synthesizedAt,
            },
          }));

        const responses = [...fromApi, ...synthesized];

        // id:0 rows are session-only placeholders; persisting them would shadow a backend fix on reload.
        const persistable = responses
          .filter(({ response }) => response.id !== 0)
          .map(({ response }) => response);
        setStoredPortCosts(persistable).catch((err) =>
          console.error('Failed to persist port costs to IDB:', err),
        );
        dispatch({ type: 'RESPONSES_RECEIVED', responses });
        // Only flip to "loaded" on the last batch — intermediate batches keep the "Loading…" toast alive instead of flickering.
        if (isLastBatch) {
          notifyLoading('Port costs loaded', {
            type: 'success',
            updateOnly: true,
            isLoading: false,
          });
        }
      } catch (error) {
        console.error('Error fetching port costs:', error);
        // Use the snapshot we actually sent — not the latest queue — so pairs added mid-flight aren't falsely marked as failed.
        const allErrors: PortCostError[] = currentQueue.map((portCostPair) => ({
          portCostPair,
          error: error instanceof Error ? error.message : String(error),
        }));
        dispatch({ type: 'ERRORS_RECEIVED', responses: allErrors });
        notifyLoading(`Failed to load ${currentQueue.length} port costs`, {
          type: 'error',
        });
      } finally {
        if (isLastBatch) {
          drainCtxRef.current = null;
        } else if (drainCtxRef.current) {
          drainCtxRef.current.consumed += currentQueue.length;
        }
        dispatch({ type: 'LOADING_ENDED' });
      }
    };

    if (queue.length > 0) {
      void fetcher();
    }
  }, [apolloClient, queue, loading, notifyLoading]);

  const queuePortCostsForPairs = useCallback(
    (portCostPairs: PortCostPair[]) => {
      if (portCostPairs.length === 0) return;
      dispatch({ type: 'ADDED_TO_QUEUE', portCostPairs });
    },
    [],
  );

  const fetchMissingMarcuraPortCosts = useCallback(
    async (
      existingPortCosts: MarcuraPortCosts,
      portIds: Set<LocationId>,
      assortmentIds: Set<number>,
    ): Promise<MarcuraPortCosts> => {
      const missingPairs: PortCostPair[] = [];

      portIds.forEach((portId) => {
        assortmentIds.forEach((assortmentId) => {
          if (!existingPortCosts.get(portId, assortmentId)) {
            missingPairs.push([portId, assortmentId]);
          }
        });
      });

      if (missingPairs.length > 0) {
        queuePortCostsForPairs(missingPairs);
      }

      return existingPortCosts;
    },
    [queuePortCostsForPairs],
  );

  const resetPortCosts = useCallback(() => {
    dispatch({ type: 'PORT_COSTS_RESET', portCosts: initialPortCostMap });
  }, []);

  const contextValue = useMemo(
    () => ({
      portCosts,
      queuePortCostsForPairs,
      fetchMissingMarcuraPortCosts,
      resetPortCosts,
    }),
    [
      portCosts,
      queuePortCostsForPairs,
      fetchMissingMarcuraPortCosts,
      resetPortCosts,
    ],
  );

  return (
    <MarcuraPortCostContext.Provider value={contextValue}>
      {children}
    </MarcuraPortCostContext.Provider>
  );
};
